import { useQueryClient } from '@tanstack/react-query';

import { useSecretMutation } from '@/hooks/use-secret-mutation';

import { clientService } from '@/services/client-service';
import type {
  CreateClientPayload,
  CreateClientResult,
} from '@/types/core/client';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { clientMutationKeys, clientQueryKeys } from './query-keys';

/**
 * Hook to create a client.
 * Returns clientId and clientSecret (secret shown once).
 * Invalidates the clients cache to trigger a refetch.
 */
export function useCreateClient(tenantId?: UUID) {
  const qc = useQueryClient();

  return useSecretMutation<CreateClientResult, CreateClientPayload>({
    mutationKey: clientMutationKeys.createClient(tenantId),
    mutationFn: async (payload: CreateClientPayload) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const result = await clientService.createClient(tenantId, payload);

      await pollForResource(() =>
        clientService.findClient(tenantId, result.clientId)
      );

      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: clientQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
